import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Grid, Card, CardMedia, CardContent, Typography, CircularProgress, Button } from "@mui/material";
import { getAdminCountries, createCountry, updateCountry } from "../../../services/Admin/CountriesApi";
import { getImageUrl } from "../../../services/indexMediaBaseUrl";
import AddCountryDialog from "./AddCountryDialog";
import EditCountryDialog from "./EditCountryDialog";

const CountriesPage = () => {
  const { i18n } = useTranslation();
  const lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  const isAr = lang.startsWith("ar");

  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // dialog state for adding country
  const [addOpen, setAddOpen] = useState(false);
  // dialog state for editing country
  const [editOpen, setEditOpen] = useState(false);
  const [editInitial, setEditInitial] = useState(null);

  const handleCreateCountry = async (formData) => {
    try {
      const res = await createCountry(formData);
      const created = res && res.data ? res.data : res;
      if (created) {
        setCountries((prev) => [created, ...prev]);
      }
      return res;
    } catch (err) {
      throw err;
    }
  };

  const handleUpdateCountry = async (formData) => {
    try {
      const res = await updateCountry(formData);
      const updated = res && res.data ? res.data : res;
      if (updated && updated.id) {
        setCountries((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
      }
      // close edit dialog on success
      setEditOpen(false);
      return res;
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getAdminCountries();
        const list = res && res.success && Array.isArray(res.data) ? res.data : [];
        setCountries(list);
      } catch (err) {
        setError(err?.message || "Error");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" onClick={() => setAddOpen(true)}>إضافة دولة</Button>
      </Box>

      <AddCountryDialog open={addOpen} onClose={() => setAddOpen(false)} onSubmit={handleCreateCountry} />
      <EditCountryDialog open={editOpen} onClose={() => setEditOpen(false)} onSubmit={handleUpdateCountry} initialData={editInitial} />

      <Grid container spacing={3}>
        {countries.map((c) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={c.id}>
            <Card sx={{ height: "100%" }}>
              <CardMedia
                component="img"
                image={getImageUrl(c.image)}
                alt={isAr ? c.name_ar : c.name_en}
                sx={{ height: 140, objectFit: "cover", backgroundColor: "#f5f5f5" }}
              />
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {isAr ? c.name_ar || c.name_en : c.name_en || c.name_ar}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {isAr ? c.currency_ar || c.currency_en : c.currency_en || c.currency_ar}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      {`Default: ${c.default ? "Yes" : "No"} • Status: ${c.status ? "Active" : "Inactive"}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {formatDate(c.created_at)}
                    </Typography>
                  </Box>
                  <Box>
                    <Button size="small" variant="outlined" sx={{ mr: 1 }} onClick={() => { setEditInitial(c); setEditOpen(true); }}>
                      تعديل
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default CountriesPage;
